import React from 'react';
import { Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderItemDto } from '../../../dto/supplierOrder';
import { formatProductDimensions } from '../../../utils/productDimensions';
import {
  formatSupplierOrderQuantity,
  formatSupplierOrderQuantityValue,
  formatSupplierOrderUnit,
} from '../supplierOrderUnits';
import './SupplierOrderProductsPreview.css';

const EMPTY_VALUE = '-';

interface SupplierOrderProductsPreviewProps {
  items?: SupplierOrderItemDto[];
  /**
   * Opt-in warehouse read-out showing `remaining / ordered` for each line.
   * Both values come from `pzReceipt`, computed across covering PZ documents
   * by the warehouse rather than re-derived here. Off by default so the plain
   * supplier-order list stays a pure "what was ordered" preview.
   */
  showPZCoverage?: boolean;
  /**
   * Explains the `remaining / ordered` warehouse figure directly next to each
   * product. The PZ transport picker supplies Polish operator copy here.
   */
  pzCoverageLabel?: string;
  /** The scoped PZ read model owns the quantity proven on this truck. */
  showPZTransportScope?: boolean;
  pzOnTransportLabel?: string;
  pzWholeOrderLabel?: string;
  pzRemainingOnTransportLabel?: string;
}

const SupplierOrderProductsPreview: React.FC<SupplierOrderProductsPreviewProps> = ({
  items,
  showPZCoverage = false,
  pzCoverageLabel,
  showPZTransportScope = false,
  pzOnTransportLabel,
  pzWholeOrderLabel,
  pzRemainingOnTransportLabel,
}) => {
  const { t, i18n } = useTranslation(['orders', 'pz']);

  if (!items?.length) {
    return <Typography.Text type="secondary">{EMPTY_VALUE}</Typography.Text>;
  }

  return (
    <div className="supplier-order-products-preview">
      {items.map((item, index) => {
        const dimensions = formatProductDimensions(item);
        const receipt = showPZCoverage ? item.pzReceipt : undefined;
        const remainingQuantity = receipt?.remainingQuantity ?? item.quantity ?? 0;
        const orderedQuantity = receipt?.quantity ?? item.quantity ?? 0;
        const fullyCovered = Boolean(receipt) && remainingQuantity <= 0;

        return (
          <div key={item.id || `${item.name}-${index}`} className="supplier-order-products-preview__item">
            <div className="supplier-order-products-preview__copy">
              <Tooltip title={item.name}>
                <span className="supplier-order-products-preview__name">{item.name}</span>
              </Tooltip>
              {dimensions ? (
                <span className="supplier-order-products-preview__dimensions">
                  {dimensions}
                </span>
              ) : null}
            </div>
            <span className="supplier-order-products-preview__figures">
              {showPZCoverage && receipt ? (
                <span className="supplier-order-products-preview__coverage">
                  {showPZTransportScope ? (
                    <span className="supplier-order-products-preview__transport-scope" title={receipt.blockReason || undefined}>
                      {receipt.quantityScope !== 'SUPPLIER_ORDER' && <span>{pzOnTransportLabel}: <strong>{formatSupplierOrderQuantityValue(orderedQuantity, i18n.language)} {formatSupplierOrderUnit(item.unit || 'pcs', t, i18n.language)}</strong></span>}
                      <span>{pzWholeOrderLabel}: <strong>{formatSupplierOrderQuantityValue(Number(item.quantity || 0), i18n.language)} {formatSupplierOrderUnit(item.unit || 'pcs', t, i18n.language)}</strong></span>
                      <span className={fullyCovered ? 'supplier-order-products-preview__quantity--full' : ''}>
                        {receipt.quantityScope === 'SUPPLIER_ORDER' ? t('pz:generateFromSupplierOrder.remainingOnSupplierOrderLabel') : pzRemainingOnTransportLabel}: <strong>{formatSupplierOrderQuantityValue(remainingQuantity, i18n.language)} {formatSupplierOrderUnit(item.unit || 'pcs', t, i18n.language)}</strong>
                      </span>
                    </span>
                  ) : (
                    <span
                      className={`supplier-order-products-preview__quantity supplier-order-products-preview__quantity--coverage${fullyCovered ? ' supplier-order-products-preview__quantity--full' : ''}`}
                      title={receipt.blockReason || undefined}
                    >
                      {`${formatSupplierOrderQuantityValue(remainingQuantity, i18n.language)} / ${formatSupplierOrderQuantityValue(orderedQuantity, i18n.language)} ${formatSupplierOrderUnit(item.unit || 'pcs', t, i18n.language)}`.trim()}
                    </span>
                  )}
                  {receipt.quantityScope === 'SUPPLIER_ORDER' && !receipt.blockReason ? (
                    <span className="supplier-order-products-preview__coverage-label">
                      {t('pz:generateFromSupplierOrder.stockReceiptQuantityHint')}
                    </span>
                  ) : null}
                  {receipt.blockReason || (receipt.quantityScope !== 'SUPPLIER_ORDER' && pzCoverageLabel) ? (
                    <span className="supplier-order-products-preview__coverage-label">
                      {receipt.blockReason || pzCoverageLabel}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="supplier-order-products-preview__quantity">
                  {item.quantity != null
                    ? formatSupplierOrderQuantity(item, t, i18n.language)
                    : EMPTY_VALUE}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default SupplierOrderProductsPreview;
